import { RefObject, useEffect, useState } from 'react';

// How close (in px) the element can get to the viewport before we load it
const ROOT_MARGIN = "1000px";

// Fallback delay for browsers without requestIdleCallback (Safari)
const IDLE_FALLBACK_MS = 2000;

/**
 * Returns true once the browser is idle after page load, or once the element is
 * within ROOT_MARGIN of the viewport, whichever comes first. Used to start loading
 * heavy embeds before the user scrolls to them, so the work doesn't happen mid-scroll.
 */
export const useLoadEarly = (ref: RefObject<Element>) => {
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		if (shouldLoad || !ref.current) return;

		const load = () => setShouldLoad(true);

		// Load when the element gets close to the viewport
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) load();
			},
			{ rootMargin: ROOT_MARGIN }
		);
		observer.observe(ref.current);

		// Or load when the browser is idle after the page finishes loading
		let idleId: number | undefined;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		const scheduleIdle = () => {
			if ("requestIdleCallback" in window) {
				idleId = window.requestIdleCallback(load, { timeout: IDLE_FALLBACK_MS * 2 });
			} else {
				timeoutId = setTimeout(load, IDLE_FALLBACK_MS);
			}
		};

		if (document.readyState === "complete") {
			scheduleIdle();
		} else {
			window.addEventListener("load", scheduleIdle, { once: true });
		}

		return () => {
			observer.disconnect();
			window.removeEventListener("load", scheduleIdle);
			if (idleId !== undefined) window.cancelIdleCallback(idleId);
			if (timeoutId !== undefined) clearTimeout(timeoutId);
		};
	}, [ref, shouldLoad]);

	return shouldLoad;
};
